import React from 'react';
import { components as selectComponents } from 'react-select';
import { useTranslation } from 'react-i18next';
import ModalShell from 'components/common/ModalShell';
import { HoverTooltip } from 'components/common/HoverTooltip';
import { AttentionIcon } from 'components/common/ProjectIcons';
import { buttonClasses } from 'components/common/tailwindClassnames';

// Lists longer than this show a summary in the form; the whole list is edited
// in a modal so hundreds of pills don't take over the page.
export const MAX_VISIBLE_PILLS = 4;

type Pill = { value: string; label: string };

type ReferencePillsOverflowProps = {
  pills: Pill[];
  staleValues?: string[];
  title: string;
  /** Shown after the title when the title names the connection. */
  fieldLabel?: string;
  disabled?: boolean;
  /**
   * The list as it was when the modal opened; null while closed. Owned by the
   * caller so the modal survives this component remounting.
   */
  saved: string[] | null;
  onSavedChange: (saved: string[] | null) => void;
  /** Puts back the values the list had when the modal opened. */
  onCancel: (saved: string[]) => void;
  /** The full multi-select, rendered inside the modal. */
  renderSelect: () => React.ReactNode;
  /** Shown under the select inside the modal (e.g. a failed-fetch Retry). */
  notice?: React.ReactNode;
};

// react-select's value container holds the pills and then the typing area.
// Only the pills scroll; the typing area stays on its own line under them.
const ScrollingValueContainer = ({ children, ...props }: any) => {
  // react-select renders the values first and the input last.
  const pills = React.Children.toArray(children);
  const input = pills.pop();
  return (
    <selectComponents.ValueContainer {...props}>
      <div
        className="flex max-h-[55vh] w-full flex-wrap overflow-y-auto"
        data-testid="ReferencePillsOverflow-pills"
      >
        {pills}
      </div>
      {input}
    </selectComponents.ValueContainer>
  );
};

// In the modal the box's border, typing area and clear button stay in view
// however long the list is.
const withScrollingPills = (select: React.ReactNode) => {
  if (!React.isValidElement(select)) return select;
  const own = (select.props as { components?: Record<string, any> }).components;
  return React.cloneElement(select as React.ReactElement<any>, {
    components: { ...own, ValueContainer: ScrollingValueContainer },
  });
};

const ReferencePillsOverflow = ({
  pills,
  staleValues = [],
  title,
  fieldLabel,
  disabled,
  saved,
  onSavedChange,
  onCancel,
  renderSelect,
  notice,
}: ReferencePillsOverflowProps) => {
  const { t } = useTranslation();
  // Edits apply to the form as they're made; `saved` is the list as it was
  // when the modal opened, so Cancel can put it back.
  const open = saved !== null;
  const done = () => onSavedChange(null);
  // Closing with the X or Escape cancels too.
  const cancel = () => {
    if (saved && !disabled) onCancel(saved);
    onSavedChange(null);
  };

  // A short list is the plain select. The modal stays open while edits shrink
  // the list (clearing it, removing pills) until Done or Cancel closes it.
  if (!open && pills.length <= MAX_VISIBLE_PILLS) return <>{renderSelect()}</>;
  const hidden = pills.length - MAX_VISIBLE_PILLS;

  return (
    <>
      <div
        className="flex min-h-[38px] flex-wrap items-center gap-1 rounded border border-unlisted-gray-lines bg-ui-background-elevated-component px-2 py-1"
        data-testid="ReferencePillsOverflow-summary"
      >
        {pills.slice(0, MAX_VISIBLE_PILLS).map((pill) => (
          <span
            key={pill.value}
            className="inline-flex max-w-[200px] items-center gap-1 rounded-sm bg-greyscale-9 px-1.5 py-0.5 text-sm text-ui-text-primary-text"
          >
            {staleValues.includes(pill.value) && (
              <HoverTooltip content={t('Saved value not in the current list')}>
                <span>
                  <AttentionIcon />
                </span>
              </HoverTooltip>
            )}
            <span className="truncate">{pill.label}</span>
          </span>
        ))}
        {hidden > 0 && (
          <span className="text-sm text-ui-text-secondary-text">
            {t('and {{count}} more', { count: hidden })}
          </span>
        )}
        <button
          type="button"
          className={`${buttonClasses.inlineLink} ml-auto`}
          onClick={() => onSavedChange(pills.map((pill) => pill.value))}
          data-testid="ReferencePillsOverflow-button"
        >
          {disabled ? t('View all') : t('Edit')}
        </button>
      </div>
      {open && (
        <ModalShell
          title={title}
          subtitle={fieldLabel}
          onClose={cancel}
          maxWidthClass="max-w-4xl"
          footer={
            <>
              <button
                type="button"
                className={buttonClasses.primary}
                onClick={done}
                data-testid="ReferencePillsOverflow-done-button"
              >
                {t('Done')}
              </button>
              {!disabled && (
                <button
                  type="button"
                  className={buttonClasses.cancel}
                  onClick={cancel}
                  data-testid="ReferencePillsOverflow-cancel-button"
                >
                  {t('Cancel')}
                </button>
              )}
            </>
          }
        >
          <p className="mb-2 text-sm text-ui-text-secondary-text">
            {t('{{count}} values', { count: pills.length })}
          </p>
          {withScrollingPills(renderSelect())}
          {notice && (
            <div data-testid="ReferencePillsOverflow-notice">{notice}</div>
          )}
        </ModalShell>
      )}
    </>
  );
};

export default ReferencePillsOverflow;
